import React, { useEffect, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import { AIChatbot } from "../../../features/ai_chatbots/aiChatbotsService";
import aiChatbotsService, { AIChatbotCopyRequest } from "../../../features/ai_chatbots/aiChatbotsService";
import companiesService, { Company } from "../../../features/companies/companiesService";
import { isAdmin, isAgencyOwner, isCompanyOwner } from "../../../utils/permissions";
import Swal from "sweetalert2";

interface AIChatbotCopyModalProps {
  show: boolean;
  onHide: () => void;
  chatbot: AIChatbot | null;
  onSuccess: () => void;
}

const AIChatbotCopyModal: React.FC<AIChatbotCopyModalProps> = ({
  show,
  onHide,
  chatbot,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { user } = useSelector((state: RootState) => state.auth);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [targetCompanyId, setTargetCompanyId] = useState<number | "">("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!show || !user) return;
    const load = async () => {
      setLoadingCompanies(true);
      try {
        if (isAdmin(user)) {
          const { companies: list } = await companiesService.getCompanies({ limit: 1000 });
          setCompanies(list || []);
        } else if (isAgencyOwner(user)) {
          const list = await companiesService.getMyAgencyCompanies();
          setCompanies(Array.isArray(list) ? list : []);
        } else if (isCompanyOwner(user)) {
          const c = await companiesService.getMyCompany();
          setCompanies(c ? [c] : []);
        } else {
          setCompanies([]);
        }
      } catch {
        setCompanies([]);
      } finally {
        setLoadingCompanies(false);
      }
    };
    load();
  }, [show, user]);

  // Modal açıldığında seçimi sıfırla; hiçbir şirket önceden seçili gelmesin
  useEffect(() => {
    if (show && chatbot) {
      setTargetCompanyId("");
      setName("");
    }
  }, [show, chatbot]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!chatbot || targetCompanyId === "") return;
    setSubmitting(true);
    try {
      const body: AIChatbotCopyRequest = {
        target_company_id: Number(targetCompanyId),
        name: name.trim() || undefined,
      };
      await aiChatbotsService.copyChatbot(chatbot.id, body);
      Swal.fire({
        title: t("success"),
        text: t("chatbot_copied_successfully"),
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });
      onSuccess();
      onHide();
    } catch (err: any) {
      Swal.fire({
        title: t("error"),
        text: err?.response?.data?.detail || err?.message || t("failed_to_copy_chatbot"),
        icon: "error",
        confirmButtonText: t("ok"),
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!chatbot) return null;

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>{t("copy_chatbot")}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <p className="text-muted mb-3">
            {t("copy_chatbot_description")} &quot;{chatbot.name}&quot;
          </p>
          <Form.Group className="mb-3">
            <Form.Label>{t("target_company")}</Form.Label>
            <Form.Select
              value={targetCompanyId}
              onChange={(e) => setTargetCompanyId(e.target.value ? Number(e.target.value) : "")}
              required
              disabled={loadingCompanies}
            >
              <option value="">{loadingCompanies ? t("loading_companies") : t("select_company")}</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.company_name} {c.company_type ? `(${c.company_type})` : ""}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t("new_chatbot_name_optional")}</Form.Label>
            <Form.Control
              type="text"
              placeholder={t("new_chatbot_name_placeholder")}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="modern-modal-footer border-0 pt-0 pb-4 justify-content-end gap-2">
          <Button
            variant="light"
            className="new-modal-btn-cancel"
            onClick={onHide}
            disabled={submitting}
          >
            {t("cancel")}
          </Button>
          <Button
            type="submit"
            className="new-modal-btn-confirm"
            disabled={submitting || targetCompanyId === ""}
          >
            {submitting ? t("loading") + "..." : t("copy")}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default AIChatbotCopyModal;
